<i18n locale="en">
{
  "description": "One plan with every tool your restaurant needs to grow and keep guests coming back.",
  "feature-cards": {
    "app": {
      "body": "Average restaurant sees 2x more reorders from app users.",
      "title": "Branded mobile app"
    },
    "reviews": {
      "body": "Owner automatically asks happy guests to leave reviews averaging 4.92 stars.",
      "title": "5-star reviews"
    },
    "sms": {
      "body": "Keep guests coming back with automated marketing campaigns.",
      "title": "Automated SMS marketing"
    },
    "upscale": {
      "body": "Make all your menu photos magazine worthy with AI photo upscaling.",
      "title": "AI photo upscaling"
    }
  },
  "plan-boost": "Boost",
  "plan-pro": "Pro",
  "price": "$499",
  "table-caption": "See all the features included with Max:",
  "table-rows": {
    "ai-cro": "AI-driven conversion rate optimization",
    "auto-email": "Automated email marketing",
    "auto-gbp": "Auto Google Business profile posts",
    "auto-sms": "Automated SMS marketing",
    "branded-app": "Custom-branded mobile app",
    "branded-email": "Branded restaurant email",
    "branded-gift-cards": "Branded gift cards",
    "catering-forms": "Catering and events forms",
    "cross-channel-loyalty": "Cross-channel loyalty program",
    "custom-email": "Customizable email marketing",
    "custom-website": "Custom branded website",
    "delivery-support": "Customer support for deliveries",
    "job-board": "Website job board",
    "listings": "Online listings management on 77+ sites",
    "live-tracking": "Live order tracking",
    "loyalty-integrations": "Loyalty & gift card integrations",
    "online-ordering": "Online ordering",
    "order-with-google": "Order with Google integration",
    "promotions": "Easy-to-use promotion tools",
    "push-marketing": "Push notification marketing",
    "reputation": "Reputation management",
    "reservations": "Reservations embed support",
    "review-engine": "Automated Google review engine",
    "review-replies": "Online review replies",
    "seo": "Search Engine Optimization (SEO)",
    "unified-inbox": "Unified inbox for customer comms",
    "upscaling": "Photo upscaling & improvements",
    "website-improver": "Website improver"
  },
  "table-sections": {
    "ai": "AI features",
    "brand": "Brand customization",
    "marketing": "Marketing",
    "ordering": "Ordering & delivery",
    "website": "Website"
  },
  "title": "More orders. More reviews. More repeat guests."
}
</i18n>
<template>
  <div class="upsell-takeover" data-testid="upsell-takeover">
    <div
      class="upsell-takeover__content mx-auto flex w-full flex-col gap-10 p-6 lg:pt-[60px] lg:pb-12"
    >
      <div class="upsell-takeover__top">
        <UpsellHero
          :title="t('title')"
          :description="t('description')"
          :price="t('price')"
          @chat="emit('chat')"
          @upgrade="emit('upgrade')"
        />
        <UpsellFeatureGrid :features="features" />
      </div>
      <PlanComparisonTable
        class="mt-10"
        :caption="t('table-caption')"
        :plans="plans"
        :sections="sections"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BubbleDots } from '@owner/icons-vue/BubbleDots'
import { Expand } from '@owner/icons-vue/Expand'
import { Phone } from '@owner/icons-vue/Phone'
import { Star } from '@owner/icons-vue/Star'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { PlanComparisonSection, UpsellFeature } from '../types'

import PlanComparisonTable from './PlanComparisonTable.vue'
import UpsellFeatureGrid from './UpsellFeatureGrid.vue'
import UpsellHero from './UpsellHero.vue'

const emit = defineEmits<{
  chat: []
  upgrade: []
}>()

const { t } = useI18n()

const CARD_ICONS = {
  app: Phone,
  reviews: Star,
  sms: BubbleDots,
  upscale: Expand,
} as const

const features = computed<UpsellFeature[]>(() =>
  (['app', 'sms', 'upscale', 'reviews'] as const).map((key) => ({
    description: t(`feature-cards.${key}.body`),
    icon: CARD_ICONS[key],
    title: t(`feature-cards.${key}.title`),
  }))
)

const plans = computed(() => [t('plan-boost'), t('plan-pro')])

const sections = computed<PlanComparisonSection[]>(() => [
  {
    rows: [
      { availability: [true, true], name: t('table-rows.website-improver') },
      { availability: [false, true], name: t('table-rows.ai-cro') },
      { availability: [false, true], name: t('table-rows.upscaling') },
      { availability: [false, true], name: t('table-rows.review-replies') },
    ],
    title: t('table-sections.ai'),
  },
  {
    rows: [
      { availability: [true, true], name: t('table-rows.seo') },
      { availability: [true, true], name: t('table-rows.custom-website') },
      { availability: [true, true], name: t('table-rows.auto-gbp') },
      { availability: [false, true], name: t('table-rows.job-board') },
      { availability: [false, true], name: t('table-rows.review-engine') },
      { availability: [false, true], name: t('table-rows.listings') },
    ],
    title: t('table-sections.website'),
  },
  {
    rows: [
      { availability: [true, true], name: t('table-rows.online-ordering') },
      { availability: [true, true], name: t('table-rows.order-with-google') },
      { availability: [true, true], name: t('table-rows.live-tracking') },
      { availability: [true, true], name: t('table-rows.delivery-support') },
      { availability: [true, true], name: t('table-rows.catering-forms') },
      { availability: [false, true], name: t('table-rows.reservations') },
    ],
    title: t('table-sections.ordering'),
  },
  {
    rows: [
      { availability: [true, true], name: t('table-rows.auto-email') },
      { availability: [true, true], name: t('table-rows.custom-email') },
      { availability: [true, true], name: t('table-rows.promotions') },
      { availability: [true, true], name: t('table-rows.reputation') },
      { availability: [false, true], name: t('table-rows.auto-sms') },
      {
        availability: [false, true],
        name: t('table-rows.loyalty-integrations'),
      },
      {
        availability: [false, true],
        name: t('table-rows.cross-channel-loyalty'),
      },
      { availability: [false, true], name: t('table-rows.unified-inbox') },
      { availability: [false, true], name: t('table-rows.push-marketing') },
    ],
    title: t('table-sections.marketing'),
  },
  {
    rows: [
      { availability: [false, true], name: t('table-rows.branded-app') },
      {
        availability: [false, true],
        name: t('table-rows.branded-gift-cards'),
      },
      { availability: [false, true], name: t('table-rows.branded-email') },
    ],
    title: t('table-sections.brand'),
  },
])
</script>
<style lang="scss" scoped>
// Full-bleed page background matching the sidebar, with the content column
// centered on top — cards and CTAs sit white-on-gray per the design.
.upsell-takeover {
  min-height: 100%;
  background-color: var(--background-secondary);

  // 854px design frame + 24px side padding on each side.
  &__content {
    max-width: 902px;
  }

  &__top {
    display: grid;
    gap: 24px;
    align-items: stretch;

    @media (min-width: 1024px) {
      grid-template-columns: 336px minmax(0, 1fr);
    }
  }
}
</style>
